/**
 * Enforced exclusivity (ADR-0046) — the client-side half of the date math. The
 * server is the authority (it validates the terms against today in the room's
 * timezone and computes `endsAt`); these only pre-fill the accept dialog and
 * run the countdown.
 */

/** Allowed exclusivity length / extension in days (mirrors the server). */
export const EXCLUSIVITY_MIN_DAYS = 1;
export const EXCLUSIVITY_MAX_DAYS = 365;
/** The accept dialog's quick picks. */
export const EXCLUSIVITY_QUICK_PICK_DAYS = [30, 60, 90, 120] as const;

/** Today's calendar date (YYYY-MM-DD) in `timeZone` (an unknown zone reads as UTC). */
export function todayInZone(timeZone: string, now: Date = new Date()): string {
  try {
    // en-CA formats as YYYY-MM-DD.
    return new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(now);
  } catch {
    return now.toISOString().slice(0, 10);
  }
}

/** `ymd` + `days` as YYYY-MM-DD — pure calendar math, no timezone. */
export function addCalendarDays(ymd: string, days: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y!, m! - 1, d! + days)).toISOString().slice(0, 10);
}

/** Time left until `endsAt`. */
export interface Remaining {
  expired: boolean;
  days: number;
  hours: number;
  minutes: number;
}

export function remainingUntil(endsAt: string | Date, now: Date = new Date()): Remaining {
  const end = endsAt instanceof Date ? endsAt : new Date(endsAt);
  const ms = Math.max(0, end.getTime() - now.getTime());
  return {
    expired: ms === 0,
    days: Math.floor(ms / 86_400_000),
    hours: Math.floor((ms % 86_400_000) / 3_600_000),
    minutes: Math.floor((ms % 3_600_000) / 60_000),
  };
}

/** "12 days, 4 hours" / "3 hours, 20 minutes" / "Ended". */
export function formatRemaining(r: Remaining): string {
  if (r.expired) return 'Ended';
  const unit = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;
  if (r.days > 0) return `${unit(r.days, 'day')}, ${unit(r.hours, 'hour')}`;
  return `${unit(r.hours, 'hour')}, ${unit(r.minutes, 'minute')}`;
}

/**
 * The validation problem with the dialog's terms, or null. Mirrors the server
 * (which re-checks): 1–365 whole days; start today or later (room time) and
 * within a year.
 */
export function exclusivityTermsProblem(
  startDate: string,
  lengthDays: number,
  today: string,
): string | null {
  if (
    !Number.isInteger(lengthDays) ||
    lengthDays < EXCLUSIVITY_MIN_DAYS ||
    lengthDays > EXCLUSIVITY_MAX_DAYS
  ) {
    return `Enter a length from ${EXCLUSIVITY_MIN_DAYS} to ${EXCLUSIVITY_MAX_DAYS} days.`;
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate)) return 'Choose the day the period starts.';
  if (startDate < today) return 'The period cannot start in the past.';
  if (startDate > addCalendarDays(today, 365)) return 'The period must start within a year.';
  return null;
}
